import { KPIMetric } from "@/app/features/reports/components/dashboard-kpi-metric";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Edit2, X } from "lucide-react";
import { useCallback } from "react";
import { KPITileConfig } from "../../types/kpi-types";
import { ReportTestItem } from "../../utils/entities";
import { calculateKPI } from "../../utils/kpi-calculator";

interface SortableKPITileProps {
  config: KPITileConfig;
  data: ReportTestItem[];
  isEditMode: boolean;
  onEdit: (config: KPITileConfig) => void;
  onRemove: (id: string) => void;
}

export function SortableKPITile({
  config,
  data,
  isEditMode,
  onEdit,
  onRemove,
}: SortableKPITileProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: config.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const value = calculateKPI(data, config);

  const handleEdit = useCallback(() => {
    onEdit(config);
  }, [config, onEdit]);

  const handleRemove = useCallback(() => {
    onRemove(config.id);
  }, [config.id, onRemove]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("relative", {
        "cursor-grab active:cursor-grabbing": isEditMode,
      })}
    >
      {isEditMode && (
        <div className="absolute top-2 right-2 z-10 flex gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-white/90 hover:bg-white shadow-sm"
            onClick={handleEdit}
          >
            <Edit2 className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 bg-white/90 hover:bg-white shadow-sm text-destructive hover:text-destructive"
            onClick={handleRemove}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}
      {isEditMode && (
        <div
          {...attributes}
          {...listeners}
          className="absolute top-2 left-2 z-10 cursor-grab active:cursor-grabbing"
        >
          <div className="h-4 w-4 bg-white/90 rounded shadow-sm flex items-center justify-center">
            <div className="h-2 w-2 bg-zinc-400 rounded" />
          </div>
        </div>
      )}
      <KPIMetric
        title={config.title}
        value={value}
        className="summary-card h-full"
      />
    </div>
  );
}

